// Sustituto de 'firebase/auth' que usa el login propio del servidor.
// La sesión se guarda en localStorage y se renueva sola.

import { apiCall, setTokenProvider, FirebaseError } from './wire.js';

const STORAGE_KEY = 'tp_session';
const listeners = new Set();
let session = null;     // { idToken, refreshToken, expiresAt, user }
let currentUser = null;
let ready = false;
let refreshTimer = null;
let refreshing = null;

function decodeJwt(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(part).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(json);
  } catch { return {}; }
}

class User {
  constructor(info) { this._apply(info); }
  _apply(info) {
    this.uid = info.uid;
    this.email = info.email || null;
    this.emailVerified = !!info.emailVerified;
    this.displayName = info.displayName || null;
    this.photoURL = null;
    this.phoneNumber = null;
    this.isAnonymous = false;
    this.providerId = 'firebase';
    this.metadata = { creationTime: info.creationTime || null, lastSignInTime: info.lastSignInTime || null };
    this.providerData = info.email ? [{ providerId: 'password', uid: info.email, email: info.email, displayName: this.displayName }] : [];
  }
  async getIdToken(forceRefresh = false) { return getValidToken(forceRefresh); }
  async getIdTokenResult(forceRefresh = false) {
    const token = await getValidToken(forceRefresh);
    const claims = decodeJwt(token);
    return {
      token,
      claims,
      authTime: new Date((claims.auth_time || 0) * 1000).toUTCString(),
      issuedAtTime: new Date((claims.iat || 0) * 1000).toUTCString(),
      expirationTime: new Date((claims.exp || 0) * 1000).toUTCString(),
      signInProvider: 'password',
      signInSecondFactor: null,
    };
  }
  async reload() {
    const { user } = await apiCall('/auth/me', null, { method: 'GET' });
    this._apply(user);
    if (session) { session.user = user; persist(); }
  }
  toJSON() { return { uid: this.uid, email: this.email, emailVerified: this.emailVerified, displayName: this.displayName }; }
}

function persist() {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* modo privado */ }
}

function notify() {
  for (const cb of listeners) {
    try { cb(currentUser); } catch (e) { console.error(e); }
  }
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  if (!session) return;
  const ms = Math.max(10_000, session.expiresAt - Date.now() - 5 * 60_000);
  refreshTimer = setTimeout(() => { getValidToken(true).catch(() => {}); }, ms);
}

function setSession(data, { silent = false } = {}) {
  const prevUid = currentUser?.uid || null;
  if (data) {
    session = {
      idToken: data.idToken,
      refreshToken: data.refreshToken,
      expiresAt: Date.now() + (data.expiresIn || 3600) * 1000,
      user: data.user,
    };
    if (currentUser && currentUser.uid === data.user.uid) currentUser._apply(data.user);
    else currentUser = new User(data.user);
  } else {
    session = null;
    currentUser = null;
  }
  authInstance.currentUser = currentUser;
  persist();
  scheduleRefresh();
  if (!silent && (currentUser?.uid || null) !== prevUid) notify();
}

async function getValidToken(force = false) {
  if (!session) return null;
  if (!force && session.expiresAt - Date.now() > 60_000) return session.idToken;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Sesión caducada o revocada: cerrar sesión
          if (res.status === 400 || res.status === 401) setSession(null);
          return null;
        }
        setSession(json, { silent: true });
        return session.idToken;
      } catch {
        return session?.idToken || null; // sin conexión: seguimos con el actual
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

setTokenProvider((force) => getValidToken(force));

const authInstance = {
  app: null,
  name: '[DEFAULT]',
  currentUser: null,
  languageCode: 'es',
  settings: {},
  onAuthStateChanged: (cb) => onAuthStateChanged(authInstance, cb),
  signOut: () => signOut(authInstance),
  useDeviceLanguage() {},
  setPersistence: async () => {},
  authStateReady: () => initPromise,
};

// Restaurar sesión guardada
const initPromise = (async () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved?.refreshToken && saved?.user) {
      session = saved;
      currentUser = new User(saved.user);
      authInstance.currentUser = currentUser;
      if (session.expiresAt - Date.now() < 60_000) await getValidToken(true);
      scheduleRefresh();
    }
  } catch { session = null; currentUser = null; }
  ready = true;
})();

// Sincronizar entre pestañas
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    try {
      const next = JSON.parse(e.newValue || 'null');
      if (!next) setSession(null);
      else {
        session = next;
        const changed = currentUser?.uid !== next.user.uid;
        if (changed) currentUser = new User(next.user);
        authInstance.currentUser = currentUser;
        scheduleRefresh();
        if (changed) notify();
      }
    } catch { /* ignorar */ }
  });
}

export function getAuth() { return authInstance; }
export const initializeAuth = getAuth;

export function onAuthStateChanged(auth, cb, onError) {
  const fn = typeof cb === 'function' ? cb : cb?.next;
  listeners.add(fn);
  initPromise.then(() => { if (listeners.has(fn)) fn(currentUser); }).catch(onError || (() => {}));
  return () => listeners.delete(fn);
}
export const onIdTokenChanged = onAuthStateChanged;

const credential = (op) => ({ user: currentUser, providerId: 'password', operationType: op });

export async function signInWithEmailAndPassword(auth, email, password) {
  const data = await apiCall('/auth/signin', { email, password }, { auth: false });
  setSession(data);
  return credential('signIn');
}

export async function signInWithCustomToken(auth, token) {
  const data = await apiCall('/auth/custom', { token }, { auth: false });
  setSession(data);
  return credential('signIn');
}

export async function signOut() {
  setSession(null);
}

export async function sendEmailVerification(user, actionCodeSettings) {
  await apiCall('/auth/send-verification', { actionCodeSettings });
}

export async function sendPasswordResetEmail(auth, email) {
  await apiCall('/fn/sendPasswordResetEmail', { data: { email } });
}

export async function createUserWithEmailAndPassword() {
  throw new FirebaseError('auth/operation-not-allowed', 'Las cuentas se crean desde el panel o con el registro.');
}

export class GoogleAuthProvider {
  constructor() { this.providerId = 'google.com'; }
  addScope() { return this; }
  setCustomParameters() { return this; }
  static credentialFromResult() { return null; }
}

export async function signInWithPopup() {
  throw new FirebaseError('auth/operation-not-allowed', 'El acceso con Google no está disponible. Entra con tu email y contraseña.');
}
export const signInWithRedirect = signInWithPopup;
export async function getRedirectResult() { return null; }

export async function setPersistence() {}
export const browserLocalPersistence = { type: 'LOCAL' };
export const browserSessionPersistence = { type: 'SESSION' };
export const inMemoryPersistence = { type: 'NONE' };
export function connectAuthEmulator() {}

export async function updatePassword() {
  throw new FirebaseError('auth/operation-not-allowed', 'Usa la opción "¿Olvidaste tu contraseña?" para cambiarla.');
}
export async function updateProfile(user, profile) {
  if (user && profile?.displayName !== undefined) user.displayName = profile.displayName;
}

export { ready as _authReady };
